import { h, Host } from "@stencil/core";
import { debounce } from "../../utils/debounce";
export class TextLinesLimit {
    constructor() {
        this.textLines = null;
        this.textLinesS = 0;
        this.textLinesM = 0;
        this.textLinesL = 0;
        this.textLinesXl = 0;
        this.readMoreText = undefined;
        this.readMoreClassName = "ux-text-link ux-text-link--green";
        this.opened = undefined;
    }
    textElement = null;
    buttonBaseClassName = "ux-text-lines-limit__btn";
    getIsReadMoreEnabled = () => typeof this.readMoreText === "string" && this.readMoreText.length > 0;
    element;
    componentDidLoad() {
        this.update();
        const buttonElement = this.element.querySelector(`.${this.buttonBaseClassName}`);
        const buttonClassNames = [this.buttonBaseClassName, ...this.readMoreClassName.split(" ")];
        if (this.getIsReadMoreEnabled()) {
            if (!buttonElement) {
                this.textElement?.insertAdjacentHTML("beforeend", `<button type="button" class="${buttonClassNames.join(" ")}">${this.readMoreText}</button>`);
            }
        }
        else if (buttonElement) {
            buttonElement.remove();
        }
    }
    connectedCallback() {
        this.setElements();
        if (this.getIsReadMoreEnabled()) {
            window.addEventListener("resize", this.updateDebounced);
        }
    }
    disconnectedCallback() {
        window.removeEventListener("resize", this.updateDebounced);
    }
    setElements = () => {
        this.textElement = this.element.querySelector("*:first-child");
    };
    getTextElementHeight = () => {
        const { textElement } = this;
        if (!textElement) {
            return 0;
        }
        const computedStyle = window.getComputedStyle(textElement);
        const clientHeight = textElement.clientHeight;
        return (clientHeight +
            parseFloat(computedStyle.getPropertyValue("margin-top")) +
            parseFloat(computedStyle.getPropertyValue("margin-bottom")));
    };
    updateExpansion = () => {
        this.element.style.removeProperty("--lines");
        /** increase (amount > 0) or decrease lines (amount < 0) */
        const adjustLinesStep = (amount) => {
            const lineCount = parseInt(window.getComputedStyle(this.element).getPropertyValue("--lines"), 10);
            this.element.style.setProperty("--lines", String(lineCount + amount));
        };
        // these values are only used to prevent infinite loops
        let counter = 0;
        const smallestLineHeight = 12;
        const expandIfPossible = () => {
            const availableHeight = this.element.clientHeight;
            if (availableHeight > this.getTextElementHeight()) {
                adjustLinesStep(1);
                if (availableHeight > this.getTextElementHeight()) {
                    if (counter++ > availableHeight / smallestLineHeight)
                        return;
                    expandIfPossible();
                }
                else {
                    adjustLinesStep(-1);
                }
            }
        };
        expandIfPossible();
    };
    updateCroppingFlag = () => {
        if (!this.textElement) {
            return;
        }
        const isCropped = this.textElement.scrollHeight > this.element.offsetHeight;
        if (isCropped) {
            this.element.removeAttribute("uncropped");
        }
        else {
            this.element.setAttribute("uncropped", "");
        }
    };
    update = () => {
        this.updateExpansion();
        this.updateCroppingFlag();
    };
    updateDebounced = debounce(this.update, 50);
    handleClick = (e) => {
        if (this.opened || !this.getIsReadMoreEnabled()) {
            return;
        }
        const button = e.target.closest(`.${this.buttonBaseClassName}`);
        if (button) {
            e.preventDefault();
            this.opened = true;
            this.element.setAttribute("open", "");
            window.removeEventListener("resize", this.updateDebounced);
        }
    };
    render() {
        const linesDefault = this.textLines || "unset";
        return (h(Host, { key: 'a377bbecefa419438bb79af5f0aeb554ae559074', onClick: this.getIsReadMoreEnabled() ? this.handleClick : null, open: this.opened || undefined, style: {
                [`--lines-default`]: `${linesDefault}`,
                [`--lines-s`]: `${this.textLinesS || linesDefault}`,
                [`--lines-m`]: `${this.textLinesM || linesDefault}`,
                [`--lines-l`]: `${this.textLinesL || linesDefault}`,
                [`--lines-xl`]: `${this.textLinesXl || linesDefault}`
            } }, h("slot", { key: 'dad1bf52fdf837cce0176548154e68d62d1c72fa' })));
    }
    static get is() { return "ux-text-lines-limit"; }
    static get originalStyleUrls() {
        return {
            "$": ["text-lines-limit.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["text-lines-limit.css"]
        };
    }
    static get properties() {
        return {
            "textLines": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number | null",
                    "resolved": "null | number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Sets the default number of lines for all viewports"
                },
                "attribute": "text-lines",
                "reflect": false,
                "defaultValue": "null"
            },
            "textLinesS": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Sets the number of lines for small viewports"
                },
                "attribute": "text-lines-s",
                "reflect": false,
                "defaultValue": "0"
            },
            "textLinesM": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Sets the number of lines for medium viewports"
                },
                "attribute": "text-lines-m",
                "reflect": false,
                "defaultValue": "0"
            },
            "textLinesL": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Sets the number of lines for large viewports"
                },
                "attribute": "text-lines-l",
                "reflect": false,
                "defaultValue": "0"
            },
            "textLinesXl": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Sets the number of lines for xl viewports"
                },
                "attribute": "text-lines-xl",
                "reflect": false,
                "defaultValue": "0"
            },
            "readMoreText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If this text is set, the component displays a button with the corresponding label.\nWhen the user clicks on this button, the entire text is displayed and the button disappears."
                },
                "attribute": "read-more-text",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "readMoreClassName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Custom class name assigned to the Read More button"
                },
                "attribute": "read-more-class-name",
                "reflect": false,
                "defaultValue": "\"ux-text-link ux-text-link--green\""
            }
        };
    }
    static get states() {
        return {
            "opened": {}
        };
    }
    static get elementRef() { return "element"; }
}
//# sourceMappingURL=text-lines-limit.js.map
